import { useState } from "react";
import API from "../../services/api";

interface User {
  _id: string;
  full_name: string;
  email: string;
  phone_number: string;
  has_active_membership: boolean;
  wallet_balance: number;
}

interface FreeMembershipModalProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}

export default function FreeMembershipModal({ user, onClose, onSuccess }: FreeMembershipModalProps) {
  const [couponCode, setCouponCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<any>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleApplyFreeMembership = async () => {
    if (!couponCode.trim()) {
      setError("Please enter a coupon code");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Single API call - validates coupon AND applies membership
      const res = await API.post("/user/free-membership", {
        userId: user._id,
        couponCode: couponCode.trim().toUpperCase()
      });

      if (res.data && res.data.success) {
        setSuccessData(res.data.data);
        setShowSuccess(true);
        setError(null);
      }
    } catch (error: any) {
      setError(error?.response?.data?.message || "Failed to apply free membership");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (showSuccess) {
      onSuccess();
    } else {
      onClose();
    }
  };

  // Success View
  if (showSuccess && successData) {
    return (
      <div style={styles.overlay} onClick={handleClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <button onClick={handleClose} style={styles.closeButton}>✕</button>
          </div>

          <div style={styles.successBox}>
            <div style={styles.successIcon}>🎉</div>
            <h3 style={styles.successTitle}>
              {successData.has_existing_membership ? "Refund Processed!" : "Membership Activated!"}
            </h3>
            <p style={styles.successMessage}>
              {successData.message}
            </p>
            
            <div style={styles.successDetails}>
              <div style={styles.detailRow}>
                <span>Coupon Applied:</span>
                <span style={styles.detailValue}>
                  <strong>{successData.coupon_applied}</strong>
                </span>
              </div>
              <div style={styles.detailRow}>
                <span>Refund Amount:</span>
                <span style={{...styles.detailValue, color: '#4caf50', fontWeight: '600'}}>
                  ₹{successData.refund_amount?.toFixed(2) || "0.00"}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span>New Wallet Balance:</span>
                <span style={{...styles.detailValue, color: '#2196f3', fontWeight: '600'}}>
                  ₹{successData.wallet_balance?.toFixed(2) || "0.00"}
                </span>
              </div>
              {successData.membership_end_date && (
                <div style={styles.detailRow}>
                  <span>Membership End Date:</span>
                  <span style={styles.detailValue}>
                    {new Date(successData.membership_end_date).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>

            <div style={styles.transactionInfo}>
              <span>Transaction ID:</span>
              <span style={styles.transactionId}>
                {successData.transaction?._id || 'N/A'}
              </span>
            </div>

            <button
              onClick={handleClose}
              style={styles.doneButton}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Form View
  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Free Membership</h2>
          <button onClick={handleClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.userInfo}>
          <div style={styles.userName}>{user.full_name}</div>
          <div style={styles.userEmail}>{user.email || user.phone_number}</div>
          <div style={styles.walletInfo}>
            Wallet Balance: <strong>₹{user.wallet_balance.toFixed(2)}</strong>
          </div>
          {user.has_active_membership ? (
            <div style={styles.membershipActive}>
              This user has an active membership
            </div>
          ) : (
            <div style={styles.membershipWarning}>
              This user does NOT have an active membership
            </div>
          )}
        </div>

        <div style={styles.stepContainer}>
          <p style={styles.instruction}>
            Enter a coupon code to get free membership. 
            {user.has_active_membership ? (
              <> The membership amount will be refunded to the wallet.</>
            ) : (
              <> 1 month membership will be activated and the amount refunded to wallet.</>
            )}
          </p>

          <div style={styles.couponInputGroup}>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              style={styles.couponInput}
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleApplyFreeMembership();
                }
              }}
            />
            <button
              onClick={handleApplyFreeMembership}
              style={{
                ...styles.applyButton,
                ...(loading ? styles.buttonDisabled : {})
              }}
              disabled={loading}
            >
              {loading ? "Processing..." : "Apply"}
            </button>
          </div>

          {error && (
            <div style={styles.errorMessage}>
              {error}
            </div>
          )}

          <div style={styles.hintText}>
            <small>💡 One coupon code can be used {user.has_active_membership ? 'for refund' : 'once per user'}</small>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "550px",
    width: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  modalTitle: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#1a2332",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#666",
    padding: "0 4px",
  },
  userInfo: {
    backgroundColor: "#f8f9fa",
    padding: "16px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  userName: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1a2332",
  },
  userEmail: {
    fontSize: "14px",
    color: "#666",
    marginTop: "4px",
  },
  walletInfo: {
    fontSize: "14px",
    color: "#555",
    marginTop: "8px",
  },
  membershipActive: {
    marginTop: "8px",
    padding: "8px 12px",
    backgroundColor: "#d4edda",
    borderRadius: "4px",
    color: "#155724",
    fontSize: "13px",
  },
  membershipWarning: {
    marginTop: "8px",
    padding: "8px 12px",
    backgroundColor: "#fff3cd",
    borderRadius: "4px",
    color: "#856404",
    fontSize: "13px",
  },
  stepContainer: {
    marginTop: "8px",
  },
  instruction: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "16px",
    lineHeight: "1.6",
  },
  couponInputGroup: {
    display: "flex",
    gap: "8px",
  },
  couponInput: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "16px",
    fontWeight: "600",
    letterSpacing: "1px",
    outline: "none",
    textTransform: "uppercase" as const,
    boxSizing: "border-box",
  },
  applyButton: {
    padding: "10px 24px",
    backgroundColor: "#6c5ce7",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    whiteSpace: "nowrap" as const,
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  successBox: {
    textAlign: "center" as const,
    padding: "8px 0",
  },
  successIcon: {
    fontSize: "56px",
    marginBottom: "8px",
  },
  successTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#4caf50",
    marginBottom: "8px",
  },
  successMessage: {
    fontSize: "14px",
    color: "#555",
    marginBottom: "16px",
    lineHeight: "1.6",
  },
  successDetails: {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "12px",
    textAlign: "left" as const,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    fontSize: "14px",
    borderBottom: "1px solid #e8ecf1",
  },
  detailValue: {
    fontWeight: "500",
  },
  couponInfo: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    fontSize: "13px",
    color: "#666",
    borderBottom: "1px solid #e8ecf1",
  },
  couponLabel: {
    color: "#666",
  },
  couponValue: {
    fontWeight: "600",
    color: "#6c5ce7",
  },
  transactionInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    backgroundColor: "#f0f7ff",
    borderRadius: "4px",
    fontSize: "12px",
    color: "#666",
    marginBottom: "20px",
  },
  transactionId: {
    fontFamily: "monospace",
    fontSize: "11px",
    color: "#333",
  },
  errorMessage: {
    marginTop: "12px",
    padding: "10px 14px",
    backgroundColor: "#fff3f3",
    border: "1px solid #f44336",
    borderRadius: "6px",
    color: "#d32f2f",
    fontSize: "14px",
  },
  hintText: {
    marginTop: "12px",
    color: "#888",
    fontSize: "12px",
  },
  doneButton: {
    padding: "10px 32px",
    backgroundColor: "#6c5ce7",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    width: "100%",
  },
};